import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import Layout from './components/Layout/SimpleLayout';
import ProductosPage from './pages/ProductosPage';
import PersonalizadoPage from './pages/PersonalizadoPage';
import TelasPage from './pages/TelasPage';
import ContactoPage from './pages/ContactoPage';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const services = [
    { icon: '👗', title: 'Vestidos', desc: 'Confección de todo tipo de vestidos a tu medida', price: 'Desde $500', gradient: 'from-pink-500/20 to-rose-500/20' },
    { icon: '👩‍🍳', title: 'Conjuntos de Cocina', desc: 'Delantales y accesorios personalizados', price: 'Desde $450', gradient: 'from-amber-500/20 to-orange-500/20' },
    { icon: '✂️', title: 'Reparaciones', desc: 'Ajustes y arreglos de prendas', price: 'Desde $100', gradient: 'from-violet-500/20 to-purple-500/20' },
  ];

  const features = [
    { icon: '⭐', title: 'Calidad Premium', desc: 'Materiales de la mejor calidad y atención al detalle' },
    { icon: '🚚', title: 'Entrega en punto medio', desc: 'Servicio en Chalco, Ixtapaluca y Amecameca' },
    { icon: '📏', title: 'A tu Medida', desc: 'Trabajos personalizados según tus especificaciones' },
  ];

  return (
    <Router>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={
            <div className="min-h-screen">
              {/* Hero Section */}
              <section className="relative overflow-hidden">
                {/* Background with gradient mesh */}
                <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-purple-50/50 to-rose-50" />
                <div className="absolute top-20 -left-20 w-72 h-72 bg-pink-400/20 rounded-full blur-[100px] animate-float" />
                <div className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-400/15 rounded-full blur-[120px] animate-float-delayed" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-rose-300/10 rounded-full blur-[150px] animate-float-slow" />
                
                <div className="relative container-custom py-20 md:py-32">
                  <div className="text-center max-w-4xl mx-auto animate-fade-in">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-pink-100/60 backdrop-blur-sm border border-pink-200/50 text-pink-700 text-sm font-medium mb-8">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mr-2 animate-pulse" />
                      Modista Profesional en tu zona
                    </div>
                    
                    <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold font-display leading-tight mb-6">
                      <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                        Creamos la moda
                      </span>
                      <br />
                      <span className="bg-gradient-to-r from-pink-600 via-rose-500 to-purple-600 bg-clip-text text-transparent">
                        de tus sueños
                      </span>
                    </h2>
                    
                    <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                      Confección de vestidos a medida, conjuntos de cocina y más. 
                      Cada pieza es una obra de arte hecha especialmente para ti.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                      <Link 
                        to="/productos" 
                        className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-2xl font-semibold shadow-xl shadow-pink-500/25 hover:shadow-2xl hover:shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-300"
                      >
                        Ver Servicios
                        <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </Link>
                      <Link 
                        to="/contacto" 
                        className="inline-flex items-center justify-center px-8 py-4 bg-white/70 backdrop-blur-sm text-gray-700 rounded-2xl font-semibold border border-gray-200/50 hover:border-pink-300 hover:bg-white hover:text-pink-600 hover:shadow-lg transition-all duration-300"
                      >
                        Contactar
                      </Link>
                    </div>
                  </div>
                </div>
              </section>

              {/* Services Preview */}
              <section className="py-20 relative">
                <div className="container-custom">
                  <div className="text-center mb-14 animate-slide-up">
                    <h3 className="text-sm font-semibold uppercase tracking-widest text-pink-500 mb-3">Nuestros Servicios</h3>
                    <h2 className="text-3xl md:text-4xl font-bold font-display text-gray-900">
                      Lo que hacemos mejor
                    </h2>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6 stagger-children">
                    {services.map((service, idx) => (
                      <div 
                        key={idx} 
                        className="group relative bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-8 hover:shadow-xl hover:shadow-pink-500/5 hover:-translate-y-2 transition-all duration-500 glow-hover"
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                        <div className="relative">
                          <div className="text-5xl mb-5 transform group-hover:scale-110 transition-transform duration-300">{service.icon}</div>
                          <h3 className="font-bold text-xl mb-2 text-gray-900">{service.title}</h3>
                          <p className="text-gray-500 mb-4 leading-relaxed">{service.desc}</p>
                          <p className="text-pink-600 font-bold text-lg">{service.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Features / Why Us */}
              <section className="py-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-50/30 to-transparent" />
                <div className="container-custom relative">
                  <div className="text-center mb-14">
                    <h3 className="text-sm font-semibold uppercase tracking-widest text-pink-500 mb-3">¿Por qué elegirnos?</h3>
                    <h2 className="text-3xl md:text-4xl font-bold font-display text-gray-900">
                      Experiencia y dedicación
                    </h2>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-8 stagger-children">
                    {features.map((f, idx) => (
                      <div key={idx} className="text-center group">
                        <div className="w-20 h-20 mx-auto mb-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-pink-100/50 flex items-center justify-center shadow-lg shadow-pink-500/5 group-hover:shadow-xl group-hover:shadow-pink-500/10 group-hover:-translate-y-1 transition-all duration-500">
                          <span className="text-4xl group-hover:scale-110 transition-transform duration-300">{f.icon}</span>
                        </div>
                        <h3 className="font-bold text-xl mb-2 text-gray-900">{f.title}</h3>
                        <p className="text-gray-500 leading-relaxed max-w-xs mx-auto">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* CTA Section */}
              <section className="py-20">
                <div className="container-custom">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pink-500 via-rose-500 to-purple-600 p-12 md:p-16">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
                    
                    <div className="relative text-center max-w-2xl mx-auto">
                      <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-4">
                        ¿Lista para tu próximo diseño?
                      </h2>
                      <p className="text-pink-100 text-lg mb-8 leading-relaxed">
                        Contáctanos hoy y transforma tus ideas en prendas únicas. 
                        Atendemos en Chalco, Ixtapaluca y Amecameca.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Link 
                          to="/personalizado" 
                          className="inline-flex items-center justify-center px-8 py-4 bg-white text-pink-600 rounded-2xl font-semibold hover:bg-pink-50 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300"
                        >
                          Solicitar Cotización
                        </Link>
                        <Link 
                          to="/contacto" 
                          className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300"
                        >
                          Más Información
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          } />
          <Route path="/productos" element={<ProductosPage />} />
          <Route path="/personalizado" element={<PersonalizadoPage />} />
          <Route path="/telas" element={<TelasPage />} />
          <Route path="/contacto" element={<ContactoPage />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;